import { useCurrentPageAccess } from "@/app/router";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { useKeywordFilter } from "@/shared/hooks";
import { useDialog } from "@/shared/hooks/useDialog";
import { RotateCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AnomalyDetectionRuleDialog } from "../components/AnomalyDetectionRuleDialog";
import { useAnomalyDetectionColumns } from "../hooks/useAnomalyDetectionColumns";
import { validateKwargsByCheckType } from "../hooks/useAnomalyDetectionRules";
import type { AnomalyDetectionRule } from "../types/preferences_types";
import { ANOMALY_CHECK_TYPES } from "../types/preferences_types";
import { CHANNELS } from "@/constants/channels";
import type { SortingState } from "@tanstack/react-table";
import {
  useAnomalyDetectionRulesQuery,
  useCheckTypesQuery,
  useCreateAnomalyDetectionRuleMutation,
  useUpdateAnomalyDetectionRuleMutation,
  useDeleteAnomalyDetectionRulesMutation,
} from "../api/anomalyDetectionRules";

export function PreferencesAnomalyDetectionRulesPage() {
  // React Query 훅들
  const { data: rules = [], isLoading, refetch } = useAnomalyDetectionRulesQuery();
  const { data: checkTypes = ANOMALY_CHECK_TYPES } = useCheckTypesQuery();

  const createMutation = useCreateAnomalyDetectionRuleMutation();
  const updateMutation = useUpdateAnomalyDetectionRuleMutation();
  const deleteMutation = useDeleteAnomalyDetectionRulesMutation();
  const { canWriteCurrentPage } = useCurrentPageAccess();

  const columns = useAnomalyDetectionColumns();

  const createDialog = useDialog<void>();
  const editDialog = useDialog<AnomalyDetectionRule>();
  const deleteDialog = useDialog<void>();

  const [selectedRows, setSelectedRows] = useState<AnomalyDetectionRule[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  const filter = useKeywordFilter(
    rules,
    (r) => [r.id.toString(), r.channel, r.reportType, r.checkType, JSON.stringify(r.kwargsByCheckType)],
    { debounceMs: 0 }
  );

  // 검색 필터 변경 시 첫 페이지로 이동
  useEffect(() => {
    setPage(1);
  }, [filter.keyword]);

  // 정렬된 데이터
  const sortedData = useMemo(() => {
    if (sorting.length === 0) return filter.filteredRows;

    const sorted = [...filter.filteredRows];
    const sort = sorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // 숫자 타입인 경우 숫자로 비교
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort.desc ? bValue - aValue : aValue - bValue;
      }

      // 객체 타입인 경우 JSON 문자열로 비교
      if (typeof aValue === 'object' || typeof bValue === 'object') {
        const aStr = JSON.stringify(aValue);
        const bStr = JSON.stringify(bValue);
        if (aStr < bStr) return sort.desc ? 1 : -1;
        if (aStr > bStr) return sort.desc ? -1 : 1;
        return 0;
      }

      // 문자열 타입인 경우 문자열로 비교
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return sort.desc ? 1 : -1;
      if (aStr > bStr) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [filter.filteredRows, sorting]);

  // 페이지네이션 핸들러
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // 페이지 크기 변경 시 첫 페이지로 이동
  };

  // 현재 페이지에 표시할 데이터 계산
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="이상 탐지 규칙 설정"
        description="수집된 데이터의 이상 패턴을 감지하는 규칙을 설정합니다. 매체별로 임계값과 조건을 설정하여 알림을 받을 수 있습니다."
      />

      <DataTable
        key={rules.length}
        columns={columns}
        data={paginatedData}
        totalCount={sortedData.length}
        page={page}
        pageSize={pageSize}
        sorting={sorting}
        onSortingChange={setSorting}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
        enableRowSelection
        rowSelectionTrigger="checkbox"
        getRowId={(row) => row.id.toString()}
        onRowSelectionChange={setSelectedRows}
        onRowClick={canWriteCurrentPage ?
          (row) => {
            setErrorMsg("");
            editDialog.open(row);
          }
          : undefined
        }
        toolbar={
          <DataTableToolbar
            searchValue={filter.keyword}
            onSearchChange={filter.setKeyword}
            showReset={!!filter.keyword}
            onReset={filter.reset}
            actions={
              <>
                <Button variant="outline" onClick={() => refetch()}>
                  <RotateCw className="mr-2 h-4 w-4" />
                  새로고침
                </Button>
                <Button
                  variant="outline"
                  disabled={!canWriteCurrentPage}
                  onClick={() => {
                    setErrorMsg("");
                    createDialog.open();
                  }}
                >
                  추가
                </Button>
                <Button
                  variant="destructive"
                  disabled={!canWriteCurrentPage || selectedRows.length === 0}
                  onClick={() => deleteDialog.open()}
                >
                  삭제
                </Button>
              </>
            }
          />
        }
      />

      <AnomalyDetectionRuleDialog
        open={createDialog.isOpen}
        onOpenChange={(open) => !open && createDialog.close()}
        mode="create"
        channels={[...CHANNELS]}
        checkTypes={checkTypes}
        errorMsg={errorMsg}
        onClearError={() => setErrorMsg("")}
        onSubmit={async ({ channel, reportType, checkType, kwargsByCheckType }) => {
          const raw = JSON.stringify(kwargsByCheckType);
          const v = validateKwargsByCheckType(checkType, raw);
          if (!v.ok) return setErrorMsg(v.error);

          await createMutation.mutateAsync({ channel, reportType, checkType, kwargsByCheckType: v.value });
          createDialog.close();
        }}
      />

      <AnomalyDetectionRuleDialog
        open={editDialog.isOpen}
        onOpenChange={(open) => !open && editDialog.close()}
        mode="edit"
        initial={editDialog.data}
        channels={[...CHANNELS]}
        checkTypes={checkTypes}
        errorMsg={errorMsg}
        onClearError={() => setErrorMsg("")}
        onSubmit={async ({ channel, reportType, checkType, kwargsByCheckType }) => {
          const target = editDialog.data;
          if (!target) return;

          const raw = JSON.stringify(kwargsByCheckType);
          const v = validateKwargsByCheckType(checkType, raw);
          if (!v.ok) return setErrorMsg(v.error);

          await updateMutation.mutateAsync({
            ruleId: target.id,
            data: { channel, reportType, checkType, kwargsByCheckType: v.value },
          });
          editDialog.close();
        }}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="규칙 삭제"
        description={`선택한 ${selectedRows.length}개의 규칙을 삭제하시겠습니까?`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={async () => {
          await deleteMutation.mutateAsync(selectedRows.map((r) => r.id));
          setSelectedRows([]);
          deleteDialog.close();
          // 삭제 후 데이터 새로고침
          await refetch();
        }}
      />
    </div>
  );
}
